import React, { useLayoutEffect, useRef, useState } from 'react';
import Button from 'apollo-react/components/Button';
import MenuItem from 'apollo-react/components/MenuItem';
import PopperMenu from 'apollo-react/components/PopperMenu';
import Tab from 'apollo-react/components/Tab';
import Tabs from 'apollo-react/components/Tabs';
import ChevronDown from 'apollo-react-icons/ChevronDown';
import StarSolid from 'apollo-react-icons/StarSolid';
import StarTab from './StarTab';
import styles from './StyledTabs.module.css';

const buildTabLabel = ({ label, isActive, showStar = true, hasError }) => (
  <span className={styles.tabLabelContent}>
    {showStar && isActive && <StarSolid className={styles.tabStarIcon} />}
    {label}
    {hasError && <span className={styles.tabErrorIndicator}>!</span>}
  </span>
);

/**
 * StyledTabs - Shared wrapper around Apollo IDS tabs.
 *
 * Accepts Apollo Tab children or legacy tab configs.
 *
 * @param {number} props.value - Active tab index
 * @param {Function} props.onChange - (event, index) => void
 * @param {Array} props.tabs - { key?, label: string|ReactNode }
 * @param {React.ReactNode} props.children - Optional Tab children
 * Other props are forwarded to Apollo Tabs.
 */
const StyledTabs = ({
  tabs,
  children,
  className,
  truncate = false,
  variant,
  ...tabsProps
}) => {
  const containerRef = useRef(null);
  const measurementRefs = useRef([]);
  const [visibleCount, setVisibleCount] = useState(Infinity);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const tabsClassName = [styles.styledTabs, className]
    .filter(Boolean)
    .join(' ');
  const tabChildren = Array.isArray(tabs)
    ? tabs.map((tab, index) => (
        <Tab
          key={tab.key ?? index}
          value={index}
          label={buildTabLabel({
            label: tab.label,
            isActive: index === tabsProps.value,
            showStar: tab.showStar !== false,
            hasError: tab.hasError,
          })}
        />
      ))
    : React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child) || child.type !== StarTab) {
          if (!React.isValidElement(child)) return child;
          return React.cloneElement(child, {
            value: child.props.value ?? index,
          });
        }

        const {
          label,
          isActive,
          showStar = true,
          hasError,
          ...tabProps
        } = child.props;

        return (
          <Tab
            {...tabProps}
            key={child.key ?? index}
            value={tabProps.value ?? index}
            label={buildTabLabel({ label, isActive, showStar, hasError })}
          />
        );
      });

  const tabList = React.Children.toArray(tabChildren);

  useLayoutEffect(() => {
    if (!truncate || !containerRef.current) {
      setVisibleCount(tabList.length);
      return undefined;
    }

    const updateVisibleTabs = () => {
      const availableWidth = containerRef.current?.clientWidth || 0;
      const tabWidths = measurementRefs.current
        .slice(0, tabList.length)
        .map((tab) => tab?.getBoundingClientRect().width || 0);
      if (!availableWidth || tabWidths.some((width) => width === 0)) return;

      const totalWidth = tabWidths.reduce((sum, width) => sum + width, 0);
      let nextVisibleCount = tabList.length;
      if (totalWidth > availableWidth) {
        const menuWidth = 100;
        let usedWidth = 0;
        nextVisibleCount = 0;
        for (const width of tabWidths) {
          if (usedWidth + width + menuWidth > availableWidth) break;
          usedWidth += width;
          nextVisibleCount += 1;
        }
      }

      setVisibleCount((currentCount) =>
        currentCount === nextVisibleCount ? currentCount : nextVisibleCount
      );
    };

    updateVisibleTabs();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateVisibleTabs);
      return () => window.removeEventListener('resize', updateVisibleTabs);
    }

    const observer = new ResizeObserver(updateVisibleTabs);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [truncate, tabList]);

  const resolvedVisibleCount = truncate
    ? Math.min(visibleCount, tabList.length)
    : tabList.length;
  const visibleTabs = tabList.slice(0, resolvedVisibleCount);
  const overflowTabs = tabList.slice(resolvedVisibleCount);
  const selectedTabVisible = visibleTabs.some(
    (tab) => tab.props.value === tabsProps.value
  );

  const handleOverflowSelect = (event, value) => {
    setMenuAnchor(null);
    tabsProps.onChange?.(event, value);
  };

  return (
    <div ref={containerRef} className={tabsClassName}>
      {truncate ? (
        <div className={styles.truncatedTabs}>
          <div className={styles.measurementTabs} aria-hidden="true">
            {tabList.map((tab, index) =>
              React.cloneElement(tab, {
                key: `measure-${tab.key ?? index}`,
                value: index,
                ref: (element) => {
                  measurementRefs.current[index] = element;
                },
                shape: 'box',
                tabIndex: -1,
              })
            )}
          </div>
          {visibleTabs.length > 0 ? (
            <Tabs
              {...tabsProps}
              value={selectedTabVisible ? tabsProps.value : false}
              variant="standard"
              shape="box"
              className={styles.visibleTabs}
            >
              {visibleTabs}
            </Tabs>
          ) : null}
          {overflowTabs.length > 0 ? (
            <>
              <Button
                variant="secondary"
                className={[
                  styles.moreButton,
                  !selectedTabVisible && styles.moreButtonSelected,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                aria-label="More tabs"
                aria-haspopup="menu"
                aria-expanded={Boolean(menuAnchor)}
              >
                More <ChevronDown />
              </Button>
              <PopperMenu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                {overflowTabs.map((tab) => (
                  <MenuItem
                    key={tab.key}
                    selected={tab.props.value === tabsProps.value}
                    onClick={(event) =>
                      handleOverflowSelect(event, tab.props.value)
                    }
                  >
                    {tab.props.label}
                  </MenuItem>
                ))}
              </PopperMenu>
            </>
          ) : null}
        </div>
      ) : (
        <Tabs {...tabsProps} variant={variant} shape="box">
          {tabList}
        </Tabs>
      )}
    </div>
  );
};

export default StyledTabs;
